import { getLotteryContract, getAccountAddress } from "../index";
import {
  getCardTypeByFrontendId,
  CARD_TYPE_LABELS,
  CARD_TYPE_EMOJI,
  HOME_BLIND_BOX_META,
} from "../constants";
import { ensureRadAllowanceForLottery } from "./tokenService";
import {
  assertWalletReady,
  formatTokenAmount,
  sendContractMethod,
} from "./txHelper";
import { shortenAddress } from "@/utils/utils";

function buildMixedTickerItems(winnerGroups) {
  const maxLen = Math.max(
    0,
    ...winnerGroups.map((group) => group.winners.length)
  );
  const items = [];

  for (let index = 0; index < maxLen; index += 1) {
    winnerGroups.forEach((group) => {
      const address = group.winners[index];
      if (!address) return;
      items.push(
        `🎉恭喜${shortenAddress(address)}获得1张${group.label}${group.emoji}`
      );
    });
  }

  return items;
}

function getLatestWinnerAddress(winners) {
  if (!winners || winners.length === 0) {
    return "暂无";
  }
  return shortenAddress(winners[winners.length - 1]);
}

export async function fetchHomeLotteryData() {
  const lottery = getLotteryContract();

  const typeResults = await Promise.all(
    HOME_BLIND_BOX_META.map(async (meta) => {
      const [winners, participants, stock] = await Promise.all([
        lottery.methods.getAllWinnerAddresses(meta.cardType).call(),
        lottery.methods.getCurrentRoomParticipants(meta.cardType).call(),
        lottery.methods.getNFTStockCount(meta.cardType).call(),
      ]);

      return {
        ...meta,
        label: CARD_TYPE_LABELS[meta.cardType],
        emoji: CARD_TYPE_EMOJI[meta.cardType],
        winners,
        participantCount: participants.length,
        stock: Number(stock),
      };
    })
  );

  const tickerItems = buildMixedTickerItems(typeResults);
  const cards = typeResults.map((item) => ({
    id: item.id,
    name: `${item.namePrefix}(${item.stock}张)`,
    image: item.image,
    price: item.displayPrice,
    participants: item.participantCount,
    latestWinner: getLatestWinnerAddress(item.winners),
  }));

  return {
    tickerItems,
    cards,
  };
}

async function fetchRoomConfig(cardType) {
  const lottery = getLotteryContract();
  const config = await lottery.methods.roomConfigs(cardType).call();
  const stakeAmount = BigInt(config.stakeAmount);
  const loseReward = BigInt(config.loseReward);
  return {
    stakeAmount,
    loseReward,
    burnAmount: BigInt(config.burnAmount),
    entryRad: formatTokenAmount(config.stakeAmount, 18, 0),
    refundRad: formatTokenAmount(String(stakeAmount + loseReward), 18, 0),
  };
}

export async function fetchBlindboxDetail(frontendId) {
  const cardType = getCardTypeByFrontendId(frontendId);
  const lottery = getLotteryContract();

  const [roomConfig, remaining, participants, historyWinners, currentRoomId] =
    await Promise.all([
      fetchRoomConfig(cardType),
      lottery.methods.getNFTStockCount(cardType).call(),
      lottery.methods.getCurrentRoomParticipants(cardType).call(),
      lottery.methods.getAllWinnerAddresses(cardType).call(),
      lottery.methods.currentRoomId(cardType).call(),
    ]);

  let hasJoined = false;
  const accountAddress = getAccountAddress();
  if (accountAddress) {
    hasJoined = await lottery.methods
      .isJoined(cardType, currentRoomId, accountAddress)
      .call();
  }

  return {
    cardType,
    remaining: Number(remaining),
    entryRad: roomConfig.entryRad,
    refundRad: roomConfig.refundRad,
    stakeAmountWei: String(roomConfig.stakeAmount),
    participants: participants.map((addr, index) => ({
      index: index + 1,
      address: addr,
      display: shortenAddress(addr),
    })),
    participantCount: participants.length,
    historyWinners: historyWinners.map((addr, index) => ({
      index: index + 1,
      address: addr,
      display: shortenAddress(addr),
    })),
    hasJoined,
    isRoomFull: participants.length >= 6,
    hasStock: Number(remaining) > 0,
  };
}

export async function participateInLottery(frontendId) {
  assertWalletReady();
  const cardType = getCardTypeByFrontendId(frontendId);
  const lottery = getLotteryContract();

  const [roomConfig, remaining, participants, currentRoomId] = await Promise.all([
    fetchRoomConfig(cardType),
    lottery.methods.getNFTStockCount(cardType).call(),
    lottery.methods.getCurrentRoomParticipants(cardType).call(),
    lottery.methods.currentRoomId(cardType).call(),
  ]);

  if (Number(remaining) <= 0) {
    throw new Error("当前盲盒奖池暂无可用卡牌");
  }
  if (participants.length >= 6) {
    throw new Error("当前房间已满，请稍后刷新");
  }

  const accountAddress = getAccountAddress();
  const joined = await lottery.methods
    .isJoined(cardType, currentRoomId, accountAddress)
    .call();
  if (joined) {
    throw new Error("您已参与当前局，请勿重复加入");
  }

  await ensureRadAllowanceForLottery(String(roomConfig.stakeAmount));
  const method = lottery.methods.participate(cardType);
  return sendContractMethod(method);
}
